import { getAvatarById, getBackgroundById } from '../assets/registry'
import { createVisionStage } from './avatarEngine/visionStage'
import lipSyncEngine from './lipSyncEngine'
// geometry manager (lazy-loaded below to avoid pulling Three.js into initial bundle)
import { getFaceMeshTriangles, warpTriangles } from './meshWarp'
import faceGeometryEngine from './faceGeometryEngine'
import { analyzeAvatarInspector } from './avatarInspector/analyzeAvatarInspector'
import {
  EYE_LAYER_POLYGONS,
  FACE_MESH_CONTOUR_PATHS,
  IRIS_INDICES
} from './avatarInspector/featureIndices'
import { FACEMESH_TRIANGULATION } from './faceMeshTriangulation'
import {
  clipToEyeSockets as eyesClipToEyeSockets,
  fillEyeSockets as eyesFillEyeSockets,
  drawPupilCircles as eyesDrawPupilCircles,
  computeIrisStats as eyesComputeIrisStats,
  drawEyelidOccluder as eyesDrawEyelidOccluder,
  sampleLidColor as eyesSampleLidColor
} from './avatarEngine/eyes'
import {
  DEFAULT_OUTPUT,
  LANDMARKS,
  FACE_MESH_POINT_COUNT,
  FACE_GEOMETRY_ANCHORS,
  NOSE_FEATURE_INDICES,
  CHEEK_FEATURE_INDICES,
  JAW_EAR_FEATURE_INDICES,
  clamp,
  lerp,
  clampByte,
  meshQualityToStride
} from './avatarEngine/helpers'
import { createCanvasAdapter } from './avatarEngine/canvasAdapter'
import { createResourceManager } from './avatarEngine/resourceManager'
import { VIDEO_CHAT_DEFAULTS } from '../config/videoChatDefaults'
import {
  createPointArray,
  computeFaceMetrics,
  toCanonical,
  canonicalToCanvas,
  applyRegionScale,
  applyRegionScaleAsymY,
  pickCanonicalPoint,
  getEyeCenter,
  avgY,
  translateIndicesY,
  translateIndicesWeighted,
  selectNearestIndices,
  readIrisCenter,
  computeEyeGaze,
  boostWeightsNear,
  buildExpressionWeights,
  buildFeatureWrapProfile,
  applyFeatureAwareWrap,
  decimateTriangles,
  computeFacialSignals
} from './avatarEngine/faceMath'
import { buildAvatarRig } from './avatarEngine/rig'
import {
  buildFaceMeshAlpha,
  buildPolygonAlphaMask,
  dilateMask,
  mergeMasksMax,
  buildEyeFallbackAlpha,
  createLayerFromAlpha,
  createScleraLayerFromAlpha,
  sampleIrisPalette,
  countOpaque,
  filterTrianglesByAlpha,
  buildBorderBackgroundAlpha
} from './avatarEngine/imageUtils'
import { buildAvatarHeadLayer } from './avatarEngine/headBuilder'
import {
  buildHeuristicHairAlpha,
  computeHairAlpha,
  buildPersonAvatarCutout
} from './avatarEngine/segmenter'
import {
  resolveAvatarSrc,
  resolveBackgroundSrc,
  loadSafeImage,
  resolveOutputDimensions,
  waitForVideoMetadata,
  refreshAvatarAssets as assetsRefreshAvatarAssets,
  refreshBackgroundAsset as assetsRefreshBackgroundAsset
} from './avatarEngine/assets'
import { createAudioProcessor } from './avatarEngine/audio'
import { buildBorderAvatarCutout as buildBorderCutout } from './avatarEngine/cutouts'
import { setRenderBudgetActive } from '@/services/renderBudget'
import {
  LIP_FEATURE_INDICES,
  EYE_FEATURE_INDICES,
  BROW_FEATURE_INDICES
} from './avatarEngine/utils'
import { createTracker } from './avatarEngine/tracker'
import {
  expandFaceOvalPointsByIndices,
  computeBrowMinY,
  captureForeheadEdgeBackup,
  restoreForeheadEdgeBackup
} from './faceMaskUtils'

const computeContainRect = (sw, sh, dw, dh) => {
  const srcW = Math.max(1, Number(sw) || 0)
  const srcH = Math.max(1, Number(sh) || 0)
  const dstW = Math.max(1, Number(dw) || 0)
  const dstH = Math.max(1, Number(dh) || 0)
  const scale = Math.min(dstW / srcW, dstH / srcH)
  const w = Math.max(1, Math.round(srcW * scale))
  const h = Math.max(1, Math.round(srcH * scale))
  const x = Math.round((dstW - w) / 2)
  const y = Math.round((dstH - h) / 2)
  return { x, y, w, h }
}

const mapLandmarksToCanvas = (landmarks, rect, canvasW, canvasH) => {
  if (!landmarks || !rect || !(canvasW > 0) || !(canvasH > 0)) return landmarks
  const count = landmarks.length
  const mapped = createPointArray(count)
  const invW = 1 / Math.max(1, canvasW)
  const invH = 1 / Math.max(1, canvasH)
  const sx = rect.w * invW
  const sy = rect.h * invH
  const ox = rect.x * invW
  const oy = rect.y * invH
  const zScale = rect.w * invW
  for (let i = 0; i < count; i += 1) {
    const lm = landmarks[i]
    if (!lm) continue
    mapped[i].x = ox + lm.x * sx
    mapped[i].y = oy + lm.y * sy
    mapped[i].z = (lm.z || 0) * zScale
  }
  return mapped
}

const isLikelyIOS = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === 'MacIntel' &&
      typeof navigator.maxTouchPoints === 'number' &&
      navigator.maxTouchPoints > 1)
  return isIOS
}

const isLikelyAndroid = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /Android/i.test(ua)
}

const isLikelyMobile = () => isLikelyIOS() || isLikelyAndroid()

const ensureCanvasInDom = (canvas, resourceManager) => {
  if (!canvas || typeof document === 'undefined') return null
  if (canvas.isConnected) return canvas.parentElement || null
  if (canvas.__attachedContainer && canvas.__attachedContainer.isConnected)
    return canvas.__attachedContainer
  try {
    const holder = document.createElement('div')
    holder.style.position = 'fixed'
    holder.style.width = '1px'
    holder.style.height = '1px'
    holder.style.opacity = '0'
    holder.style.pointerEvents = 'none'
    holder.style.left = '-10000px'
    holder.style.top = '-10000px'
    holder.style.zIndex = '-1'
    holder.appendChild(canvas)
    document.body && document.body.appendChild(holder)
    canvas.__attachedContainer = holder
    if (resourceManager && typeof resourceManager.registerDisposable === 'function') {
      resourceManager.registerDisposable(() => {
        try {
          if (holder && holder.parentNode) holder.parentNode.removeChild(holder)
        } catch {}
      })
    }
    return holder
  } catch {
    return null
  }
}

const safeCaptureStream = (canvas, fps) => {
  if (!canvas || typeof canvas.captureStream !== 'function') return null
  try {
    return canvas.captureStream(fps)
  } catch {
    return null
  }
}

class AvatarEngine {
  constructor() {
    this._initialized = false
    this._faceLandmarker = null
    this._faceLandmarkerImage = null
    this._segmenter = null
    this._segmenterImage = null
    this._hairSegmenterImage = null
    this._initPromise = null
    this._vision = null
    this._lastVisionError = null
    this._settings = {}
  }

  _needsVisionResources({ mode = 'both', defaults }) {
    const needImage = mode === 'image' || mode === 'both'
    const needVideo = mode === 'video' || mode === 'both'
    if (!this._vision) return true
    if (!defaults.disableFace) {
      if (needVideo && !this._faceLandmarker) return true
      if (needImage && !this._faceLandmarkerImage) return true
    }
    if (!defaults.disableSegmentation) {
      if (needVideo && !this._segmenter) return true
      if (needImage && !this._segmenterImage) return true
    }
    if (!defaults.disableAvatarHair && needImage && !this._hairSegmenterImage) return true
    return false
  }

  async init(options = {}) {
    const defaults = {
      wasmPath:
        options.wasmPath || 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm',
      faceModelPath:
        options.faceModelPath ||
        'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
      segmentModelPath:
        options.segmentModelPath ||
        'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite',
      hairModelPath:
        options.hairModelPath ||
        'https://storage.googleapis.com/mediapipe-models/image_segmenter/hair_segmenter/float32/latest/hair_segmenter.tflite',
      disableFace: options.disableFace === true,
      disableSegmentation: options.disableSegmentation === true,
      disableAvatarHair: options.disableAvatarHair === true
    }

    this._settings = { ...options }
    const requestedMode = options.visionMode || 'both'
    const shouldInit = this._needsVisionResources({ mode: requestedMode, defaults })

    if (!shouldInit && this._initialized && !options.force)
      return this._initPromise || Promise.resolve()

    if (this._initPromise) {
      await this._initPromise
      const needsAfterWait = this._needsVisionResources({ mode: requestedMode, defaults })
      if (!needsAfterWait && this._initialized && !options.force)
        return this._initPromise || Promise.resolve()
    }

    this._initPromise = (async () => {
      if (typeof window === 'undefined') return
      try {
        // lazy-load vision module to keep initial bundle small
        const visionMod = await import('./avatarEngine/vision')
        const ensureVision = visionMod.ensureVisionResources || visionMod.initVision
        const visionInit = await ensureVision(
          {
            vision: this._vision,
            faceLandmarker: this._faceLandmarker,
            faceLandmarkerImage: this._faceLandmarkerImage,
            segmenter: this._segmenter,
            segmenterImage: this._segmenterImage,
            hairSegmenterImage: this._hairSegmenterImage
          },
          defaults,
          { mode: requestedMode }
        )
        this._vision = visionInit?.vision || this._vision
        this._faceLandmarker = visionInit?.faceLandmarker || this._faceLandmarker
        this._faceLandmarkerImage = visionInit?.faceLandmarkerImage || this._faceLandmarkerImage
        this._segmenter = visionInit?.segmenter || this._segmenter
        this._segmenterImage = visionInit?.segmenterImage || this._segmenterImage
        this._hairSegmenterImage = visionInit?.hairSegmenterImage || this._hairSegmenterImage

        this._initialized = true
      } catch (err) {
        this._lastVisionError = err
        console.warn('[AvatarEngine] MediaPipe init failed', err)
      }
    })()

    try {
      await this._initPromise
    } finally {
      // allow future staged upgrades
      this._initPromise = null
    }

    return true
  }

  getStatus() {
    return {
      initialized: this._initialized,
      faceLandmarker: !!this._faceLandmarker,
      faceLandmarkerImage: !!this._faceLandmarkerImage,
      segmenter: !!this._segmenter,
      hairSegmenter: !!this._hairSegmenterImage,
      lastError: this._lastVisionError
    }
  }

  listBuiltInAvatars() {
    return []
  }

  listBuiltInBackgrounds() {
    return []
  }

  async buildAvatarInspectorLayers(avatarSrc, options = {}) {
    if (!avatarSrc || typeof avatarSrc !== 'string') return null
    if (typeof document === 'undefined') return null

    const enableHairSegmenter = options.enableHairSegmenter !== false
    await this.init({
      ...options,
      visionMode: options.visionMode || 'image',
      // Enable image segmenter for cleaner background/foreground separation
      disableSegmentation: false,
      disableAvatarHair: !enableHairSegmenter
    })
    return analyzeAvatarInspector({
      avatarSrc,
      faceLandmarkerImage: this._faceLandmarkerImage,
      segmenterImage: this._segmenterImage,
      hairSegmenterImage: enableHairSegmenter ? this._hairSegmenterImage : null,
      hairEnhance: options.hairEnhance === true,
      hairEdgeQuality: ['off', 'smooth', 'supersample'].includes(options.hairEdgeQuality)
        ? options.hairEdgeQuality
        : 'smooth',
      hairMaskSampling: ['nearest', 'bilinear'].includes(options.hairMaskSampling)
        ? options.hairMaskSampling
        : 'bilinear',
      // Forward inspector background/foreground extraction controls
      backgroundThreshold: options.backgroundThreshold,
      edgeFeather: options.edgeFeather,
      foregroundDilate: options.foregroundDilate,
      extractBackground: options.extractBackground,
      extractForeground: options.extractForeground
    })
  }

  async createTransformedStream(inputStream, options = {}) {
    if (!inputStream) throw new Error('inputStream is required')

    if (typeof document === 'undefined') {
      return { stream: inputStream, stop: () => {} }
    }

    // Ensure full vision stack for live video processing.
    await this.init({ ...options, visionMode: 'both' })

    const hasLayerVisibility =
      options.layerVisibility && typeof options.layerVisibility === 'object'
    const providedOutput =
      options.output && typeof options.output === 'object' ? options.output : {}
    const hasExplicitOutputSize =
      Number.isFinite(Number(providedOutput.width)) &&
      Number(providedOutput.width) > 0 &&
      Number.isFinite(Number(providedOutput.height)) &&
      Number(providedOutput.height) > 0
    const initialLayerVisibility = {
      background: hasLayerVisibility ? options.layerVisibility.background !== false : true,
      camera: hasLayerVisibility
        ? options.layerVisibility.camera === true
        : options.showCamera === true,
      avatarFace: hasLayerVisibility ? options.layerVisibility.avatarFace !== false : true,
      avatarHair: hasLayerVisibility
        ? options.layerVisibility.avatarHair !== false
        : options.avatarHair !== false,
      avatarEyes: hasLayerVisibility ? options.layerVisibility.avatarEyes !== false : true,
      meshContours: false
      // meshIrises removed: vector-only iris rendering
    }

    const settings = {
      avatarId: options.avatarId || '',
      avatarSrc: options.avatarSrc || null,
      backgroundId: options.backgroundId || '',
      backgroundSrc: options.backgroundSrc || null,
      precomputedAvatarCutoutSrc:
        typeof options.precomputedAvatarCutoutSrc === 'string' &&
        options.precomputedAvatarCutoutSrc.length
          ? options.precomputedAvatarCutoutSrc
          : null,
      precomputedAvatarHairMaskSrc:
        typeof options.precomputedAvatarHairMaskSrc === 'string' &&
        options.precomputedAvatarHairMaskSrc.length
          ? options.precomputedAvatarHairMaskSrc
          : null,
      overlayMode: options.overlayMode || 'auto',
      useSegmentation: options.useSegmentation !== false,
      avatarCutoutMode: ['auto', 'border', 'person', 'object'].includes(options.avatarCutoutMode)
        ? options.avatarCutoutMode
        : 'auto',
      avatarCutoutQuality: ['fast', 'balanced', 'high'].includes(options.avatarCutoutQuality)
        ? options.avatarCutoutQuality
        : 'balanced',
      featherPx: clamp(Number(options.featherPx) || 3, 0, 12),
      temporalSmoothing: ['off', 'ema', 'rvm'].includes(options.temporalSmoothing)
        ? options.temporalSmoothing
        : 'ema',
      eyePreserve: options.eyePreserve === true,
      showCamera: initialLayerVisibility.camera,
      headFollow: options.headFollow !== false,
      animateMouth: options.animateMouth !== false,
      expressionWarp: options.expressionWarp !== false,
      expressionStrength: Number(options.expressionStrength) || 1.0,
      irisStyle: options.irisStyle === 'image' ? 'image' : 'vector',
      irisColor: typeof options.irisColor === 'string' ? options.irisColor : null,
      browStrength: Number.isFinite(Number(options.browStrength))
        ? clamp(Number(options.browStrength), 0, 2)
        : 2.0,
      browSmoothing: clamp(Number(options.browSmoothing) || 0.2, 0, 0.95),
      browFrownStrength: Number.isFinite(Number(options.browFrownStrength))
        ? clamp(Number(options.browFrownStrength), 0, 2)
        : 1.0,
      browPinchStrength: Number.isFinite(Number(options.browPinchStrength))
        ? clamp(Number(options.browPinchStrength), 0, 2)
        : 1.0,
      eyeFrownStrength: Number.isFinite(Number(options.eyeFrownStrength))
        ? clamp(Number(options.eyeFrownStrength), 0, 2)
        : 1.0,
      cheekStrength: Number.isFinite(Number(options.cheekStrength))
        ? clamp(Number(options.cheekStrength), 0, 2)
        : 1.0,
      cheekSmoothing: clamp(Number(options.cheekSmoothing) || 0.45, 0, 0.95),
      // Hard-disable mesh overlay in camera unless explicitly allowed
      faceMeshRender:
        options.allowDebugOverlays === true && typeof options.faceMeshRender === 'string'
          ? options.faceMeshRender
          : 'none',
      canonicalSmoothing: clamp(Number(options.canonicalSmoothing) || 0.2, 0, 0.95),
      warpSmoothing: clamp(Number(options.warpSmoothing) || 0.2, 0, 0.95),
      geometrySmoothing: clamp(Number(options.geometrySmoothing) || 0.25, 0, 0.95),
      // Eyelid occluder & iris-freeze thresholds (can be tuned via updateSettings)
      lidCloseStart: clamp(Number(options.lidCloseStart) || 0.5, 0, 1),
      lidCloseFull: clamp(Number(options.lidCloseFull) || 0.85, 0, 1),
      lidColor: typeof options.lidColor === 'string' ? options.lidColor : null,
      irisFreezeStart: clamp(Number(options.irisFreezeStart) || 0.6, 0, 1),
      irisFreezeFull: clamp(Number(options.irisFreezeFull) || 0.8, 0, 1),
      // Detect a fully closed eye from landmark aperture (canonical units)
      eyeAutoCloseEnabled: options.eyeAutoCloseEnabled !== false,
      eyeAutoCloseLow: Number.isFinite(Number(options.eyeAutoCloseLow))
        ? Number(options.eyeAutoCloseLow)
        : 0.018,
      eyeAutoCloseHigh: Number.isFinite(Number(options.eyeAutoCloseHigh))
        ? Number(options.eyeAutoCloseHigh)
        : 0.028,
      // Gaze calibration and dynamics
      gazeDeadzone: Number.isFinite(Number(options.gazeDeadzone))
        ? Number(options.gazeDeadzone)
        : 0.04,
      gazeSmoothing: Number.isFinite(Number(options.gazeSmoothing))
        ? Number(options.gazeSmoothing)
        : 0.6,
      gazeOffsetLX: Number(options.gazeOffsetLX) || 0,
      gazeOffsetLY: Number(options.gazeOffsetLY) || 0,
      gazeGainLX: Number.isFinite(Number(options.gazeGainLX)) ? Number(options.gazeGainLX) : 0.9,
      gazeGainLY: Number.isFinite(Number(options.gazeGainLY)) ? Number(options.gazeGainLY) : 1.1,
      gazeOffsetRX: Number(options.gazeOffsetRX) || 0,
      gazeOffsetRY: Number(options.gazeOffsetRY) || 0,
      gazeGainRX: Number.isFinite(Number(options.gazeGainRX)) ? Number(options.gazeGainRX) : 0.9,
      gazeGainRY: Number.isFinite(Number(options.gazeGainRY)) ? Number(options.gazeGainRY) : 1.1,
      eye: {
        scleraCenter: options.eye?.scleraCenter || '#F6F9FC',
        scleraEdge: options.eye?.scleraEdge || '#D7DDE6',
        irisColor: options.eye?.irisColor || null,
        irisBorderColor: options.eye?.irisBorderColor || '#22303A',
        pupilColor: options.eye?.pupilColor || '#0A0E16'
      },
      useFaceGeometry: options.useFaceGeometry !== false,
      faceGeometryBlend: clamp(Number(options.faceGeometryBlend) || 0.72, 0, 1),
      faceFitStrength: clamp(Number(options.faceFitStrength) || 0.62, 0, 1),
      avatarHair: initialLayerVisibility.avatarHair,
      hairEnhance: options.hairEnhance === true,
      hairEdgeQuality: ['off', 'smooth', 'supersample'].includes(options.hairEdgeQuality)
        ? options.hairEdgeQuality
        : 'smooth',
      hairMaskSampling: ['nearest', 'bilinear'].includes(options.hairMaskSampling)
        ? options.hairMaskSampling
        : 'bilinear',
      layerVisibility: initialLayerVisibility,
      autoFitFace: options.autoFitFace !== false,
      avatarScale: Number.isFinite(Number(options.avatarScale)) ? Number(options.avatarScale) : 0,
      mouthBoost: clamp(Number(options.mouthBoost) || 1.0, 0.5, 1.8),
      mouthSmoothing: clamp(Number(options.mouthSmoothing) || 0.6, 0, 0.95),
      detectInterval: Number(options.detectInterval) || 33,
      segmentationInterval: clamp(Number(options.segmentationInterval) || 90, 35, 220),
      segmentationMaskScale: clamp(Number(options.segmentationMaskScale) || 0.5, 0.25, 1),
      segmentationMaskThreshold: clamp(Number(options.segmentationMaskThreshold) || 0.5, 0, 1),
      segmentationInvertMask:
        options.segmentationInvertMask === true
          ? true
          : options.segmentationInvertMask === false
            ? false
            : null,
      segmentationDebugOverlay: options.segmentationDebugOverlay === true,
      segmentationTuning:
        options.segmentationTuning && typeof options.segmentationTuning === 'object'
          ? {
              probThreshold: clamp(Number(options.segmentationTuning.probThreshold) || 0.5, 0, 1),
              edgeFeather: Math.max(
                0,
                Math.round(Number(options.segmentationTuning.edgeFeather) || 0)
              ),
              foregroundDilate: Math.round(Number(options.segmentationTuning.foregroundDilate) || 0)
            }
          : null,
      adaptiveQuality: options.adaptiveQuality !== false,
      adaptiveTargetFps:
        Number(options.adaptiveTargetFps) ||
        (isLikelyMobile() ? 20 : Number(providedOutput.fps) || DEFAULT_OUTPUT.fps),
      adaptiveMinFps: Number(options.adaptiveMinFps) || (isLikelyMobile() ? 12 : 18),
      adaptiveMaxFps: Number(options.adaptiveMaxFps) || (isLikelyMobile() ? 24 : 30),
      faceUpdateInterval: Number(options.faceUpdateInterval) || 25,
      meshWorkerEnabled:
        typeof options.meshWorkerEnabled === 'boolean'
          ? options.meshWorkerEnabled
          : VIDEO_CHAT_DEFAULTS.meshWorkerEnabled,
      meshQuality: ['high', 'balanced', 'performance'].includes(options.meshQuality)
        ? options.meshQuality
        : 'high',
      geometryDepth: clamp(Number(options.geometryDepth) || 0.42, 0, 1),
      lipSyncProfile: options.lipSyncProfile || null,
      debugLogs: options.debugLogs === true,
      onDebug: typeof options.onDebug === 'function' ? options.onDebug : null,
      output: {
        ...providedOutput,
        quality:
          providedOutput.quality ||
          providedOutput.avatarCutoutQuality ||
          options.avatarCutoutQuality ||
          'balanced',
        fps: Number.isFinite(Number(providedOutput.fps))
          ? Number(providedOutput.fps)
          : DEFAULT_OUTPUT.fps
      },
      onFrame: options.onFrame || null
    }
    // If a precomputed Foreground is provided, we prefer it as the authoritative
    // foreground, but we don't forcibly disable the worker compositor here so
    // composite/offscreen rendering can still be used to match the inspector
    // when supported.
    settings._hasExplicitOutputSize = hasExplicitOutputSize
    const mobileProfile = options.mobileProfile !== false && isLikelyMobile()
    if (mobileProfile) {
      if (!Number.isFinite(Number(options.segmentationInterval)))
        settings.segmentationInterval = Math.max(90, Number(settings.segmentationInterval) || 90)
      if (!Number.isFinite(Number(options.faceUpdateInterval)))
        settings.faceUpdateInterval = Math.max(35, Number(settings.faceUpdateInterval) || 35)
      if (!Number.isFinite(Number(options.detectInterval)))
        settings.detectInterval = Math.max(45, Number(settings.detectInterval) || 45)
      if (!Number.isFinite(Number(options.segmentationMaskScale)))
        settings.segmentationMaskScale = 0.4
      if (!options.meshQuality) settings.meshQuality = 'performance'
      settings.meshWorkerEnabled = false
      if (typeof options.useFaceGeometry !== 'boolean') settings.useFaceGeometry = false
      if (!Number.isFinite(Number(options.faceGeometryBlend))) settings.faceGeometryBlend = 0
      if (!Number.isFinite(Number(options.geometryDepth)))
        settings.geometryDepth = Math.min(0.35, Number(settings.geometryDepth) || 0.35)
      if (!(options.output && Number.isFinite(Number(options.output.fps)))) {
        settings.output.fps = Math.min(Number(settings.output.fps) || DEFAULT_OUTPUT.fps, 20)
      }
    }
    settings._adaptiveBase = {
      outputFps: Number(settings.output?.fps) || DEFAULT_OUTPUT.fps,
      segInterval: Number(settings.segmentationInterval) || 90,
      maskScale: Number(settings.segmentationMaskScale) || 0.5,
      meshQuality: settings.meshQuality || 'high',
      faceUpdateInterval: Number(settings.faceUpdateInterval) || 40
    }
    settings._adaptiveLevel = 0

    const debug = (message) => {
      if (!settings.debugLogs) return
      if (settings.onDebug) settings.onDebug(message)
      else console.log('[AvatarEngine]', message)
    }
    // Remember overlay permission for later updates
    settings._allowDebugOverlays = options.allowDebugOverlays === true

    // buildHeuristicHairAlpha moved to ./avatarEngine/segmenter

    const videoEl = document.createElement('video')
    videoEl.playsInline = true
    videoEl.muted = true
    videoEl.srcObject = inputStream

    await videoEl.play().catch(() => {})
    await waitForVideoMetadata(videoEl)

    const resolvedOutput = resolveOutputDimensions(
      settings.output,
      videoEl.videoWidth,
      videoEl.videoHeight,
      hasExplicitOutputSize
    )
    settings.output = {
      ...settings.output,
      ...resolvedOutput
    }

    const _canvasAdapter = createCanvasAdapter()
    // Ensure final output canvas is a DOM canvas so captureStream is supported
    const canvas = _canvasAdapter.createDOMCanvas(resolvedOutput.width, resolvedOutput.height)
    canvas.width = resolvedOutput.width
    canvas.height = resolvedOutput.height
    const compositeRequested =
      settings.meshWorkerEnabled === true && settings.meshWorkerComposite !== false
    const compositeSupported =
      compositeRequested &&
      typeof canvas.transferControlToOffscreen === 'function' &&
      typeof OffscreenCanvas !== 'undefined'
    let ctx =
      compositeSupported || typeof canvas.getContext !== 'function'
        ? null
        : canvas.getContext('2d') || _canvasAdapter.getContext(canvas, '2d')
    const resourceManager = createResourceManager({ canvasAdapter: _canvasAdapter })
    // register output canvas for lifecycle management
    if (resourceManager && typeof resourceManager.registerCanvas === 'function') {
      const skipZero = compositeSupported === true
      resourceManager.registerCanvas(canvas, { skipZero })
      if (!skipZero) {
        resourceManager.registerDisposable(() => {
          try {
            canvas.width = 0
            canvas.height = 0
          } catch (e) {}
        })
      }
    }

    const ensureGeometryRenderer = () =>
      geometryManager && typeof geometryManager.ensure === 'function'
        ? geometryManager.ensure()
        : null

    let avatarImage = { img: null, safe: false }
    let bgImage = { img: null, safe: false }

    let avatarRig = null
    let featureWrapProfile = null

    let avatarLayers = null
    let rendererManager = null
    let simpleCutoutCanvas = null
    let simpleCutoutKey = ''
    // Optional precomputed foreground cutout (e.g., from inspector)
    let precomputedCutoutCanvas = null
    let precomputedCutoutKey = ''
    let geometryAssetsVersion = 0
    let layerPreviewCacheVersion = -1
    let layerPreviewCache = null
    let geometryManager = {
      ensure: () => null,
      resetApplied: () => {},
      getRenderer: () => null
    }
    ;(async () => {
      try {
        const geomMod = await import('./avatarEngine/geometry')
        const createGeometryManager = geomMod.default || geomMod.createGeometryManager || geomMod
        geometryManager = createGeometryManager({
          getCanvas: () => canvas,
          getAvatarRig: () => avatarRig,
          getAvatarLayers: () => avatarLayers,
          getSettings: () => settings,
          getCurrentAvatarSrc: () => currentAvatarSrc,
          getGeometryAssetsVersion: () => geometryAssetsVersion,
          getDebug: () => (msg) => debug && debug(msg),
          getRuntimeState: () =>
            tracker && typeof tracker.getLatestState === 'function' ? tracker.getLatestState() : {},
          setMeshOutputs: (o = {}) => {
            try {
              if (o.dstPoints) warpDstPoints = o.dstPoints
              if (o.dstDepth) warpDepthBuffer = o.dstDepth
              if (o.eyeWarpDstPoints) eyeWarpDstPoints = o.eyeWarpDstPoints
              if (o.eyeSocketDstPoints) eyeSocketDstPoints = o.eyeSocketDstPoints
            } catch (e) {}
          },
          setResourceManager: (rm) => {
            try {
              if (rm && typeof rm.registerCanvas === 'function') {
                // allow geometry renderer to register internals via provided resource manager
                if (typeof rm.setResourceManager === 'function') rm.setResourceManager(rm)
              }
            } catch (e) {}
          },
          resourceManager
        })
      } catch (e) {
        debug && debug('Failed to lazy-load geometry module: ' + (e && e.message))
      }
    })()
    // let isSwappingAvatar = false

    // buildAvatarHeadLayer moved to ./avatarEngine/headBuilder

    // Face tracking
    const faceLandmarker = this._faceLandmarker
    const segmenter = this._segmenter
    let faceDetector = null
    let lastFaceBox = null
    let lastFaceLandmarks = null
    let lastDetectAt = 0
    // tracker delegates face detection, blendshapes, motion fallback and canonical processing
    const tracker = createTracker({
      getVideoEl: () => videoEl,
      getSettings: () => settings,
      getFaceLandmarker: () => this._faceLandmarker,
      getFaceDetector: () => faceDetector,
      getCanvasAdapter: () => _canvasAdapter,
      getAvatarRig: () => avatarRig,
      resourceManager
    })
    // supply resourceManager so internal canvases (motion canvas, etc.) can be registered
    // (patch applied below to add resourceManager arg)
    let trackedLiveCanonical = null
    let trackedFacialSignals = null
    let baselineCanonical = null
    let targetCanonical = null
    let targetCanonicalSmoothed = null
    let fittedCanonical = null
    let geometryCanonicalBuffer = null
    let geometryCanonicalSmoothed = null
    let warpCanonicalBuffer = null
    let warpDepthBuffer = null
    let warpDstPoints = null
    let baselineCanvasPoints = null
    let eyeWarpDstPoints = null
    let eyeSocketDstPoints = null
    let currentFaceGeometryPoseMatrix = null
    let autoFitScale = 1
    let currentAvatarSrc = null
    let currentBackgroundSrc = null
    let currentAvatarHair = settings.avatarHair
    let faceTriangleWarpCache = { source: null, stride: 1, triangles: null }
    let visualUpdatePromise = Promise.resolve()

    const refreshBackgroundAsset = async ({ force = false } = {}) => {
      const res = await assetsRefreshBackgroundAsset({
        settings,
        currentBackgroundSrc,
        force,
        debug
      })
      if (!res || !res.changed) return false
      currentBackgroundSrc = res.nextBackgroundSrc
      bgImage = res.bgImage
      return true
    }

    // Some mobile browsers (notably iOS Safari) require the canvas to be in the DOM
    // for captureStream to produce frames.
    const shouldAttachCanvas =
      settings.forceCanvasInDom === true ||
      typeof canvas.captureStream !== 'function' ||
      isLikelyMobile()
    if (shouldAttachCanvas) {
      ensureCanvasInDom(canvas, resourceManager)
      settings._canvasInDom = true
    }

    const buildBorderAvatarCutout = () => {
      if (!avatarImage?.img) return null
      try {
        const w = avatarImage.img.naturalWidth || avatarImage.img.width
        const h = avatarImage.img.naturalHeight || avatarImage.img.height
        if (!(w > 0 && h > 0)) return null
        const key = `${currentAvatarSrc}:${w}x${h}:border:${settings.featherPx}`
        if (simpleCutoutCanvas && simpleCutoutKey === key) return simpleCutoutCanvas
        // Pass segmenterImage to enable proper person detection
        const canvas = buildBorderCutout({
          avatarImage,
          settings,
          segmenterImage: this._segmenterImage
        })
        simpleCutoutCanvas = canvas
        simpleCutoutKey = key
        return canvas
      } catch {
        return null
      }
    }

    const buildPersonAvatarCutout = () => {
      if (!avatarImage?.img || !this._segmenterImage) return null
      try {
        const w = avatarImage.img.naturalWidth || avatarImage.img.width
        const h = avatarImage.img.naturalHeight || avatarImage.img.height
        if (!(w > 0 && h > 0)) return null
        const key = `${currentAvatarSrc}:${w}x${h}:person:${settings.featherPx}`
        if (simpleCutoutCanvas && simpleCutoutKey === key) return simpleCutoutCanvas
        const canvas = buildPersonAvatarCutout(avatarImage, this._segmenterImage, w, h, settings)
        simpleCutoutCanvas = canvas
        simpleCutoutKey = key
        return canvas
      } catch {
        return null
      }
    }

    const buildAvatarCutoutByMode = () => {
      // If a precomputed foreground cutout is available, use it first.
      if (precomputedCutoutCanvas) return precomputedCutoutCanvas
      const mode = settings.avatarCutoutMode || 'auto'
      if (mode === 'border') return buildBorderAvatarCutout()
      if (mode === 'person') return buildPersonAvatarCutout() || buildBorderAvatarCutout()
      if (mode === 'object') {
        // TODO: ONNX Runtime Web + u2netp integration. For now, fallback.
        return buildBorderAvatarCutout()
      }
      // auto
      return buildPersonAvatarCutout() || buildBorderAvatarCutout()
    }

    const refreshAvatarAssets = async ({ force = false } = {}) => {
      const res = await assetsRefreshAvatarAssets({
        settings,
        currentAvatarSrc,
        currentAvatarHair,
        faceLandmarkerImage: this._faceLandmarkerImage,
        hairSegmenterImage: this._hairSegmenterImage,
        force,
        debug
      })
      if (!res || !res.changed) return false
      try {
        const {
          nextAvatarSrc,
          nextImage,
          nextRig,
          nextLayers,
          featureWrapProfile: nextFeatureWrap
        } = res
        // Perform atomic swap to new assets
        currentAvatarSrc = nextAvatarSrc
        currentAvatarHair = settings.avatarHair
        avatarImage = nextImage
        avatarRig = nextRig
        featureWrapProfile = nextFeatureWrap
        avatarLayers = nextLayers

        // Signal worker cache invalidation when assets change
        if (rendererManager && typeof rendererManager.invalidateWorkerAssets === 'function') {
          rendererManager.invalidateWorkerAssets({ immediate: true })
        }

        // Reset simple cutout cache when avatar changes
        simpleCutoutCanvas = null
        simpleCutoutKey = ''
        // Invalidate any precomputed cutout; will rebuild below if provided
        precomputedCutoutCanvas = null
        precomputedCutoutKey = ''
        geometryAssetsVersion += 1
        layerPreviewCacheVersion = -1
        layerPreviewCache = null
        debug &&
          debug('[refreshAvatarAssets] avatar assets swapped successfully', {
            currentAvatarSrc,
            geometryAssetsVersion
          })
        try {
          geometryManager.resetApplied()
        } catch (e) {}
        trackedLiveCanonical = null
        trackedFacialSignals = null
        baselineCanonical = null
        targetCanonical = null
        targetCanonicalSmoothed = null
        fittedCanonical = null
        geometryCanonicalBuffer = null
        geometryCanonicalSmoothed = null
        warpCanonicalBuffer = null
        warpDepthBuffer = null
        warpDstPoints = null
        eyeWarpDstPoints = null
        eyeSocketDstPoints = null
        autoFitScale = 1
        faceTriangleWarpCache = { source: null, stride: 1, triangles: null }
        // Prepare precomputed cutout canvas if provided and sized to this avatar
        try {
          const src = settings.precomputedAvatarCutoutSrc
          if (src && typeof src === 'string' && avatarImage?.img) {
            const key = `${currentAvatarSrc}:${src}`
            if (precomputedCutoutKey !== key) {
              const loaded = await loadSafeImage(src)
              if (loaded?.img) {
                const w =
                  avatarImage.img.naturalWidth ||
                  avatarImage.img.width ||
                  loaded.img.naturalWidth ||
                  loaded.img.width ||
                  0
                const h =
                  avatarImage.img.naturalHeight ||
                  avatarImage.img.height ||
                  loaded.img.naturalHeight ||
                  loaded.img.height ||
                  0
                if (w > 0 && h > 0) {
                  const c = document.createElement('canvas')
                  c.width = w
                  c.height = h
                  const cx = c.getContext('2d')
                  if (cx) {
                    cx.clearRect(0, 0, w, h)
                    cx.drawImage(loaded.img, 0, 0, w, h)
                    precomputedCutoutCanvas = c
                    precomputedCutoutKey = key
                    // try {
                    //   console.info('[engine] built precomputedCutoutCanvas', {
                    //     key: precomputedCutoutKey,
                    //     w: c.width,
                    //     h: c.height
                    //   })
                    // } catch {}
                    try {
                      if (typeof window !== 'undefined')
                        window.__precomputedCutoutCanvas = precomputedCutoutCanvas
                    } catch {}
                  }
                }
              }
            }
          }
        } catch {}
        return true
      } catch (e) {
        debug && debug('[refreshAvatarAssets] failed to swap avatar assets: ' + (e && e.message))
        return false
      }
    }

    const refreshVisualAssets = async ({ force = false } = {}) => {
      await refreshBackgroundAsset({ force })
      await refreshAvatarAssets({ force })
    }

    // drawContourPoints removed with debug overlay removal

    // Debug contour drawing removed in live preview

    await refreshVisualAssets({ force: true })

    // Detection/tracking delegated to `tracker` module (MediaPipe/FaceDetector/motion)

    // Smoothed blendshape-based blink & gaze (fallback to landmark-based if unavailable)
    let bsBlinkL = 0
    let bsBlinkR = 0
    let blendshapeSignals = null
    let bsLookLX = 0,
      bsLookLY = 0
    let bsLookRX = 0,
      bsLookRY = 0
    const bsK = 0.6 // EMA smoothing
    // Auto-closure smoothing
    let acBlinkL = 0
    let acBlinkR = 0
    let acLatchL = false
    let acLatchR = false
    let acFramesL = 0
    let acFramesR = 0
    // Smoothed calibrated gaze
    let gSmoothLX = 0,
      gSmoothLY = 0,
      gSmoothRX = 0,
      gSmoothRY = 0
    // Smoothed brow raise (per-eye/side)
    let browSmoothL = 0
    let browSmoothR = 0
    // Smoothed cheek lift (per side)
    let cheekSmoothL = 0
    let cheekSmoothR = 0

    const updateFace = async (nowOverride) => {
      if (!videoEl.videoWidth || !videoEl.videoHeight) return
      // delegate detection/tracking to tracker module which handles MP/FaceDetector/motion
      try {
        const nowMs = Number.isFinite(nowOverride) ? nowOverride : performance.now()
        const res = await tracker.processFrame({
          now: nowMs,
          canvasWidth: videoEl.videoWidth,
          canvasHeight: videoEl.videoHeight,
          mouthLevel
        })
        // map tracked outputs back into local state used by renderer
        if (res) {
          if (res.lastFaceLandmarks) lastFaceLandmarks = res.lastFaceLandmarks
          if (res.lastFaceBox) lastFaceBox = res.lastFaceBox
          if (Object.prototype.hasOwnProperty.call(res, 'liveCanonical')) {
            trackedLiveCanonical = Array.isArray(res.liveCanonical) ? res.liveCanonical : null
          }
          if (Object.prototype.hasOwnProperty.call(res, 'facialSignals')) {
            trackedFacialSignals =
              res.facialSignals && typeof res.facialSignals === 'object' ? res.facialSignals : null
          }
          if (Object.prototype.hasOwnProperty.call(res, 'baselineCanonical')) {
            baselineCanonical = Array.isArray(res.baselineCanonical) ? res.baselineCanonical : null
          }
          if (typeof res.bsBlinkL === 'number') bsBlinkL = res.bsBlinkL
          if (typeof res.bsBlinkR === 'number') bsBlinkR = res.bsBlinkR
          if (res.blendshapeSignals && typeof res.blendshapeSignals === 'object')
            blendshapeSignals = res.blendshapeSignals
        }
      } catch (e) {
        /* fallback: preserve existing state */
      }
    }

    // eyelid color sampling moved to shared eyes helper (uses adapter)

    // Audio / Lip Sync (moved to audio helper)
    let audioContext = null
    let analyser = null
    let analyserData = null
    let mouthLevel = 0
    let lipSyncNode = null
    try {
      const audioProc = await createAudioProcessor({ inputStream, settings, debug, lipSyncEngine })
      if (audioProc) {
        audioContext = audioProc.audioContext
        analyser = audioProc.analyser
        analyserData = audioProc.analyserData
        lipSyncNode = audioProc.lipSyncNode
        if (resourceManager && typeof resourceManager.registerDisposable === 'function') {
          resourceManager.registerDisposable(() => {
            try {
              audioProc.dispose && audioProc.dispose()
            } catch (e) {}
          })
        }
      }
    } catch (e) {
      analyser = null
    }

    // Segmentation mask helpers
    let maskCanvas = null
    let maskCtx = null
    let maskImageData = null
    let segmentedCanvas = null
    let segmentedCtx = null
    let lastSegmentationAt = 0

    const drawSegmentedVideo = ({ allowRawFallback = true } = {}) => {
      const ensureMask = (width, height) => {
        if (!width || !height) return false
        if (!maskCanvas || maskCanvas.width !== width || maskCanvas.height !== height) {
          maskCanvas = _canvasAdapter.createCanvas(width, height)
          maskCanvas.width = width
          maskCanvas.height = height
          maskCtx = _canvasAdapter.getContext(maskCanvas, '2d', { willReadFrequently: true })
          maskImageData = maskCtx.createImageData(width, height)
        }
        return true
      }

      const writeAlphaMask = (width, height, alphaAt) => {
        if (!ensureMask(width, height)) return false
        const pixelCount = width * height
        for (let p = 0; p < pixelCount; p += 1) {
          const a = clamp(alphaAt(p), 0, 255)
          const di = p * 4
          maskImageData.data[di] = 255
          maskImageData.data[di + 1] = 255
          maskImageData.data[di + 2] = 255
          maskImageData.data[di + 3] = a
        }
        maskCtx.putImageData(maskImageData, 0, 0)
        return true
      }

      const segInterval = Math.max(35, Number(settings.segmentationInterval) || 90)
      const now = performance.now()
      const needMaskUpdate = !maskCanvas || now - lastSegmentationAt >= segInterval

      if (needMaskUpdate) {
        const seg = segmenter?.segmentForVideo(videoEl, now)
        let didUpdateMask = false

        const confidenceMasks = seg?.confidenceMasks
        if (confidenceMasks?.length) {
          const personMask = confidenceMasks[1] || confidenceMasks[0]
          const floats = personMask?.getAsFloat32Array?.()
          const width = personMask?.width || 0
          const height = personMask?.height || 0
          if (floats?.length && width * height === floats.length) {
            didUpdateMask = writeAlphaMask(width, height, (p) => {
              const confidence = clamp(floats[p] || 0, 0, 1)
              const soft = Math.pow(confidence, 0.75)
              return Math.round(255 * soft)
            })
          }
        }

        if (!didUpdateMask) {
          const categoryMask = seg?.categoryMask?.getAsImageData?.()
          if (categoryMask?.data?.length) {
            const width = categoryMask.width
            const height = categoryMask.height
            let maxValue = 0
            const sampleStride = Math.max(1, Math.floor(categoryMask.data.length / 4096))
            for (let i = 0; i < categoryMask.data.length; i += 4 * sampleStride) {
              if (categoryMask.data[i] > maxValue) maxValue = categoryMask.data[i]
            }
            didUpdateMask = writeAlphaMask(width, height, (p) => {
              const raw = categoryMask.data[p * 4] || 0
              const isPerson = maxValue <= 2 ? raw > 0 : maxValue <= 8 ? raw > 2 : raw >= 128
              return isPerson ? 255 : 0
            })
          }
        }

        if (didUpdateMask) {
          lastSegmentationAt = now
        }
      }

      if (!maskCanvas) {
        if (allowRawFallback) {
          const rect = computeContainRect(
            videoEl?.videoWidth || canvas.width,
            videoEl?.videoHeight || canvas.height,
            canvas.width,
            canvas.height
          )
          ctx.drawImage(videoEl, rect.x, rect.y, rect.w, rect.h)
        }
        return
      }

      if (
        !segmentedCanvas ||
        segmentedCanvas.width !== canvas.width ||
        segmentedCanvas.height !== canvas.height
      ) {
        segmentedCanvas = _canvasAdapter.createCanvas(canvas.width, canvas.height)
        segmentedCanvas.width = canvas.width
        segmentedCanvas.height = canvas.height
        segmentedCtx = _canvasAdapter.getContext(segmentedCanvas, '2d', {
          willReadFrequently: true
        })
      }

      segmentedCtx.clearRect(0, 0, segmentedCanvas.width, segmentedCanvas.height)
      try {
        segmentedCtx.imageSmoothingEnabled = true
        if (typeof segmentedCtx.imageSmoothingQuality === 'string')
          segmentedCtx.imageSmoothingQuality = 'high'
      } catch {}
      const rect = computeContainRect(
        videoEl?.videoWidth || segmentedCanvas.width,
        videoEl?.videoHeight || segmentedCanvas.height,
        segmentedCanvas.width,
        segmentedCanvas.height
      )
      segmentedCtx.drawImage(videoEl, rect.x, rect.y, rect.w, rect.h)
      segmentedCtx.globalCompositeOperation = 'destination-in'
      try {
        const base = Math.max(0, Math.min(24, Number(settings.featherPx) || 0))
        const upscale = Math.max(
          segmentedCanvas.width / Math.max(1, maskCanvas.width),
          segmentedCanvas.height / Math.max(1, maskCanvas.height)
        )
        const auto = Math.max(0, Math.round((upscale - 1) * 3.5))
        const feather = Math.max(base, auto, 2)
        segmentedCtx.filter = feather > 0 ? `blur(${feather}px)` : 'none'
      } catch {}
      segmentedCtx.drawImage(maskCanvas, rect.x, rect.y, rect.w, rect.h)
      try {
        segmentedCtx.filter = 'none'
      } catch {}
      segmentedCtx.globalCompositeOperation = 'source-over'

      // Draw segmented person over whatever is already on the output canvas (background/avatar layers).
      ctx.drawImage(segmentedCanvas, 0, 0, canvas.width, canvas.height)
    }

    let renderLoop = null
    // Legacy monolithic renderStep removed. Rendering is owned by avatarEngine/renderer.

    // Create and start render loop (throttles to settings.output.fps)
    let createRenderer
    try {
      const mod = await import('./avatarEngine/renderer')
      createRenderer = mod && (mod.createRenderer || mod.default?.createRenderer)
    } catch (e) {
      const msg = '[AvatarEngine] Failed dynamic import: ./avatarEngine/renderer'
      try {
        console.error(msg, e)
      } catch {}
      throw new Error(msg)
    }
    rendererManager = createRenderer({
      getCanvas: () => canvas,
      getCtx: () => {
        if (
          !ctx &&
          !(
            settings.meshWorkerEnabled === true &&
            settings.meshWorkerComposite !== false &&
            settings.meshWorkerCompositeFailed !== true
          )
        ) {
          try {
            ctx = canvas.getContext('2d')
          } catch {}
          if (!ctx) {
            try {
              ctx = _canvasAdapter.getContext(canvas, '2d')
            } catch {}
          }
        }
        return ctx
      },
      getSettings: () => settings,
      getVideoEl: () => videoEl,
      getSegmenter: () => segmenter,
      getAvatarImage: () => avatarImage,
      getAvatarRig: () => avatarRig,
      getAvatarLayers: () => avatarLayers,
      getPrecomputedCutoutCanvas: () => precomputedCutoutCanvas,
      getGeometryManager: () => geometryManager,
      getBackgroundImage: () => bgImage,
      buildCutoutByMode: () => buildAvatarCutoutByMode(),
      buildBorderCutout: () => buildBorderAvatarCutout(),
      updateFace,
      getRuntimeState: () => {
        const t =
          tracker && typeof tracker.getLatestState === 'function' ? tracker.getLatestState() : {}
        return {
          ...t,
          mouthLevel,
          lipSyncNode,
          analyser,
          analyserData,
          faceGeometryEngine,
          currentFaceGeometryPoseMatrix,
          // propagate mesh outputs for consumers
          warpDstPoints,
          warpDepthBuffer,
          eyeWarpDstPoints,
          eyeSocketDstPoints
        }
      },
      resolveOutputDimensions,
      debug
    })

    let createRenderLoop
    try {
      const mod = await import('./avatarEngine/renderLoop')
      createRenderLoop = mod && (mod.createRenderLoop || mod.default?.createRenderLoop)
    } catch (e) {
      const msg = '[AvatarEngine] Failed dynamic import: ./avatarEngine/renderLoop'
      try {
        console.error(msg, e)
      } catch {}
      throw new Error(msg)
    }
    renderLoop = createRenderLoop(
      () => Math.max(12, Number(settings.output?.fps) || DEFAULT_OUTPUT.fps),
      // Step: run renderer (or fallback). Rendering functions handle precomputed
      // cutouts themselves (including worker/composite cases) so avoid an
      // early return here; defer to the renderer implementation.
      (now) => {
        if (rendererManager?.renderStep) rendererManager.renderStep(now)
      },
      {
        getVideoEl: () => videoEl,
        preferVideoFrameCallback: true
      }
    )
    renderLoop.start()
    try {
      setRenderBudgetActive('avatar', true)
    } catch {}

    const hasVideoTracks = (stream) => {
      try {
        return (stream?.getVideoTracks?.() || []).length > 0
      } catch {
        return false
      }
    }

    let outputStream = safeCaptureStream(canvas, settings.output.fps)
    if (!hasVideoTracks(outputStream)) {
      try {
        console.warn(
          '[AvatarEngine] canvas.captureStream unavailable or empty; using fallback and retrying'
        )
      } catch {}
      const fallbackStream = new MediaStream()
      const fallbackTracks =
        inputStream && typeof inputStream.getVideoTracks === 'function'
          ? inputStream.getVideoTracks()
          : []
      fallbackTracks.forEach((t) => {
        try {
          fallbackStream.addTrack(t)
        } catch {}
      })
      outputStream = fallbackStream

      let attempts = 0
      const maxAttempts = 6
      let retryTimer = null
      const retry = () => {
        if (attempts >= maxAttempts) return
        attempts += 1
        const next = safeCaptureStream(canvas, settings.output.fps)
        if (hasVideoTracks(next)) {
          try {
            const current = outputStream.getVideoTracks() || []
            current.forEach((t) => outputStream.removeTrack(t))
          } catch {}
          try {
            next.getVideoTracks().forEach((t) => outputStream.addTrack(t))
          } catch {}
          return
        }
        retryTimer = setTimeout(retry, 500)
      }
      retryTimer = setTimeout(retry, 400)
      if (resourceManager && typeof resourceManager.registerDisposable === 'function') {
        resourceManager.registerDisposable(() => {
          try {
            if (retryTimer) clearTimeout(retryTimer)
          } catch {}
          retryTimer = null
        })
      }
    }
    // Include original microphone audio tracks in the output so WebRTC can send voice
    try {
      const micTracks =
        inputStream && typeof inputStream.getAudioTracks === 'function'
          ? inputStream.getAudioTracks()
          : []
      micTracks.forEach((t) => {
        try {
          outputStream.addTrack(t)
        } catch {}
      })
    } catch {}

    const updateSettings = async (next = {}) => {
      if (!next || typeof next !== 'object') return { ...settings }
      const prevMeshQuality = settings.meshQuality
      const prevHairEnhance = settings.hairEnhance
      const prevHairEdgeQuality = settings.hairEdgeQuality
      const prevHairMaskSampling = settings.hairMaskSampling
      const prevMouthRestClose = settings.mouthRestClose === true
      const prevMouthRestScale = Number.isFinite(Number(settings.mouthRestScale))
        ? Number(settings.mouthRestScale)
        : null
      const forceAvatarRefresh = next.forceAvatarRefresh === true
      const clampFps = (v, d) => {
        const n = Number(v)
        return Number.isFinite(n) ? clamp(n, 12, 60) : d
      }
      if (!settings.layerVisibility || typeof settings.layerVisibility !== 'object') {
        settings.layerVisibility = {
          background: true,
          camera: !!settings.showCamera,
          avatarFace: true,
          avatarHair: settings.avatarHair !== false,
          avatarEyes: true,
          meshContours: false
          // meshIrises removed
        }
      }

      const scalarFields = [
        'avatarId',
        'avatarSrc',
        'precomputedAvatarCutoutSrc',
        'precomputedAvatarHairMaskSrc',
        'backgroundId',
        'backgroundSrc',
        'overlayMode',
        'useSegmentation',
        'avatarCutoutMode',
        'avatarCutoutQuality',
        'featherPx',
        'temporalSmoothing',
        'eyePreserve',
        'showCamera',
        'headFollow',
        'animateMouth',
        'expressionWarp',
        'expressionStrength',
        'irisStyle',
        'irisColor',
        'browStrength',
        'browFrownStrength',
        'browPinchStrength',
        'eyeFrownStrength',
        'cheekStrength',
        'faceMeshRender',
        'eyeAutoCloseLow',
        'eyeAutoCloseHigh',
        'lidCloseStart',
        'lidCloseFull',
        'lidColor',
        'irisFreezeStart',
        'irisFreezeFull',
        'gazeDeadzone',
        'gazeSmoothing',
        'gazeOffsetLX',
        'gazeOffsetLY',
        'gazeGainLX',
        'gazeGainLY',
        'gazeOffsetRX',
        'gazeOffsetRY',
        'gazeGainRX',
        'gazeGainRY',
        'useFaceGeometry',
        'faceGeometryBlend',
        'faceFitStrength',
        'avatarHair',
        'hairEnhance',
        'hairEdgeQuality',
        'hairMaskSampling',
        'autoFitFace',
        'avatarScale',
        'mouthBoost',
        'mouthOpenGain',
        'mouthCloseGain',
        'mouthScaleMin',
        'mouthScaleMax',
        'mouthRegionX',
        'mouthRegionY',
        'mouthRestClose',
        'mouthRestScale',
        'detectInterval',
        'segmentationInterval',
        'segmentationMaskScale',
        'segmentationMaskThreshold',
        'segmentationInvertMask',
        'segmentationDebugOverlay',
        'adaptiveQuality',
        'adaptiveTargetFps',
        'adaptiveMinFps',
        'adaptiveMaxFps',
        'faceUpdateInterval',
        'meshWorkerEnabled',
        'meshWorkerComposite',
        'meshQuality',
        'geometryDepth',
        'lipSyncProfile',
        'debugLogs',
        'onFrame'
      ]

      for (const key of scalarFields) {
        if (!(key in next)) continue
        if (key === 'precomputedAvatarCutoutSrc') {
          const v =
            typeof next.precomputedAvatarCutoutSrc === 'string' &&
            next.precomputedAvatarCutoutSrc.length
              ? next.precomputedAvatarCutoutSrc
              : null
          settings.precomputedAvatarCutoutSrc = v
          // Rebuild precomputed canvas immediately when provided
          try {
            precomputedCutoutCanvas = null
            precomputedCutoutKey = ''
            try {
              if (settings && settings.debugLogs) {
                console.debug('[engine] cleared precomputedCutoutCanvas')
              }
            } catch {}
            if (v && avatarImage?.img) {
              const key = `${currentAvatarSrc}:${v}`
              const loaded = await loadSafeImage(v)
              if (loaded?.img) {
                const w =
                  avatarImage.img.naturalWidth ||
                  avatarImage.img.width ||
                  loaded.img.naturalWidth ||
                  loaded.img.width ||
                  0
                const h =
                  avatarImage.img.naturalHeight ||
                  avatarImage.img.height ||
                  loaded.img.naturalHeight ||
                  loaded.img.height ||
                  0
                if (w > 0 && h > 0) {
                  const c = document.createElement('canvas')
                  c.width = w
                  c.height = h
                  const cx = c.getContext('2d')
                  if (cx) {
                    cx.clearRect(0, 0, w, h)
                    cx.drawImage(loaded.img, 0, 0, w, h)
                    precomputedCutoutCanvas = c
                    precomputedCutoutKey = key
                  }
                }
              }
            }
          } catch {}
          continue
        }
        if (key === 'precomputedAvatarHairMaskSrc') {
          const v =
            typeof next.precomputedAvatarHairMaskSrc === 'string' &&
            next.precomputedAvatarHairMaskSrc.length
              ? next.precomputedAvatarHairMaskSrc
              : null
          settings.precomputedAvatarHairMaskSrc = v
          continue
        }
        if (key === 'avatarCutoutMode') {
          settings.avatarCutoutMode = ['auto', 'border', 'person', 'object'].includes(
            next.avatarCutoutMode
          )
            ? next.avatarCutoutMode
            : settings.avatarCutoutMode
          continue
        }
        if (key === 'avatarCutoutQuality') {
          settings.avatarCutoutQuality = ['fast', 'balanced', 'high'].includes(
            next.avatarCutoutQuality
          )
            ? next.avatarCutoutQuality
            : settings.avatarCutoutQuality
          continue
        }
        if (key === 'featherPx') {
          settings.featherPx = clamp(Number(next.featherPx) || 0, 0, 12)
          continue
        }
        if (key === 'temporalSmoothing') {
          settings.temporalSmoothing = ['off', 'ema', 'rvm'].includes(next.temporalSmoothing)
            ? next.temporalSmoothing
            : settings.temporalSmoothing
          continue
        }
        if (key === 'eyePreserve') {
          settings.eyePreserve = next.eyePreserve === true
          continue
        }
        if (key === 'segmentationInterval') {
          settings.segmentationInterval = clamp(Number(next.segmentationInterval) || 90, 35, 220)
          continue
        }
        if (key === 'segmentationMaskScale') {
          settings.segmentationMaskScale = clamp(Number(next.segmentationMaskScale) || 0.5, 0.25, 1)
          continue
        }
        if (key === 'segmentationMaskThreshold') {
          settings.segmentationMaskThreshold = clamp(
            Number(next.segmentationMaskThreshold) || 0.5,
            0,
            1
          )
          continue
        }
        if (key === 'segmentationInvertMask') {
          settings.segmentationInvertMask =
            next.segmentationInvertMask === true
              ? true
              : next.segmentationInvertMask === false
                ? false
                : null
          continue
        }
        if (key === 'segmentationDebugOverlay') {
          settings.segmentationDebugOverlay = next.segmentationDebugOverlay === true
          continue
        }
        if (key === 'adaptiveQuality') {
          settings.adaptiveQuality = next.adaptiveQuality !== false
          continue
        }
        if (key === 'adaptiveTargetFps') {
          settings.adaptiveTargetFps = clampFps(next.adaptiveTargetFps, settings.adaptiveTargetFps)
          continue
        }
        if (key === 'adaptiveMinFps') {
          settings.adaptiveMinFps = clampFps(next.adaptiveMinFps, settings.adaptiveMinFps)
          continue
        }
        if (key === 'adaptiveMaxFps') {
          settings.adaptiveMaxFps = clampFps(next.adaptiveMaxFps, settings.adaptiveMaxFps)
          continue
        }
        if (key === 'faceUpdateInterval') {
          settings.faceUpdateInterval = clampFps(
            next.faceUpdateInterval,
            settings.faceUpdateInterval
          )
          continue
        }
        if (key === 'detectInterval') {
          const n = Number(next.detectInterval)
          settings.detectInterval = Number.isFinite(n) ? clamp(n, 16, 200) : settings.detectInterval
          continue
        }
        if (key === 'meshWorkerEnabled') {
          settings.meshWorkerEnabled = next.meshWorkerEnabled === true
          continue
        }
        if (key === 'meshQuality') {
          settings.meshQuality = ['high', 'balanced', 'performance'].includes(next.meshQuality)
            ? next.meshQuality
            : settings.meshQuality
          continue
        }
        if (key === 'avatarScale') {
          settings.avatarScale = Number.isFinite(Number(next.avatarScale))
            ? Number(next.avatarScale)
            : settings.avatarScale
          continue
        }
        if (key === 'mouthRestClose') {
          settings.mouthRestClose = next.mouthRestClose === true
          continue
        }
        if (key === 'mouthRestScale') {
          const raw = Number(next.mouthRestScale)
          settings.mouthRestScale = Number.isFinite(raw)
            ? Math.max(0, Math.min(1, raw))
            : settings.mouthRestScale
          continue
        }
        if (key === 'showCamera') {
          settings.showCamera = next.showCamera === true
          settings.layerVisibility.camera = settings.showCamera
          continue
        }
        if (key === 'geometryDepth') {
          settings.geometryDepth = clamp(Number(next.geometryDepth) || 0.42, 0, 1)
          continue
        }
        if (key === 'faceGeometryBlend') {
          settings.faceGeometryBlend = clamp(Number(next.faceGeometryBlend) || 0.72, 0, 1)
          continue
        }
        if (key === 'useFaceGeometry') {
          settings.useFaceGeometry = next.useFaceGeometry !== false
          continue
        }
        if (key === 'faceFitStrength') {
          settings.faceFitStrength = clamp(Number(next.faceFitStrength) || 0.62, 0, 1)
          continue
        }
        if (key === 'avatarHair') {
          settings.avatarHair = next.avatarHair !== false
          settings.layerVisibility.avatarHair = settings.avatarHair
          continue
        }
        if (key === 'hairEnhance') {
          settings.hairEnhance = next.hairEnhance === true
          continue
        }
        if (key === 'hairEdgeQuality') {
          settings.hairEdgeQuality = ['off', 'smooth', 'supersample'].includes(next.hairEdgeQuality)
            ? next.hairEdgeQuality
            : settings.hairEdgeQuality
          continue
        }
        if (key === 'hairMaskSampling') {
          settings.hairMaskSampling = ['nearest', 'bilinear'].includes(next.hairMaskSampling)
            ? next.hairMaskSampling
            : settings.hairMaskSampling
          continue
        }
        if (key === 'irisColor') {
          settings.irisColor =
            typeof next.irisColor === 'string' ? next.irisColor : settings.irisColor
          continue
        }
        if (key === 'browStrength') {
          settings.browStrength = clamp(
            Number(next.browStrength) || settings.browStrength || 1,
            0,
            2
          )
          continue
        }
        if (key === 'browSmoothing') {
          settings.browSmoothing = clamp(
            Number(next.browSmoothing) || settings.browSmoothing || 0.5,
            0,
            0.95
          )
          continue
        }
        if (key === 'canonicalSmoothing') {
          settings.canonicalSmoothing = clamp(
            Number(next.canonicalSmoothing) || settings.canonicalSmoothing || 0.2,
            0,
            0.95
          )
          continue
        }
        if (key === 'warpSmoothing') {
          settings.warpSmoothing = clamp(
            Number(next.warpSmoothing) || settings.warpSmoothing || 0.2,
            0,
            0.95
          )
          continue
        }
        if (key === 'geometrySmoothing') {
          settings.geometrySmoothing = clamp(
            Number(next.geometrySmoothing) || settings.geometrySmoothing || 0.25,
            0,
            0.95
          )
          continue
        }
        if (key === 'browFrownStrength') {
          settings.browFrownStrength = clamp(
            Number(next.browFrownStrength) || settings.browFrownStrength || 1,
            0,
            2
          )
          continue
        }
        if (key === 'browPinchStrength') {
          settings.browPinchStrength = clamp(
            Number(next.browPinchStrength) || settings.browPinchStrength || 1,
            0,
            2
          )
          continue
        }
        if (key === 'eyeFrownStrength') {
          settings.eyeFrownStrength = clamp(
            Number(next.eyeFrownStrength) || settings.eyeFrownStrength || 1,
            0,
            2
          )
          continue
        }
        if (key === 'cheekStrength') {
          settings.cheekStrength = clamp(
            Number(next.cheekStrength) || settings.cheekStrength || 1,
            0,
            2
          )
          continue
        }
        if (key === 'cheekSmoothing') {
          settings.cheekSmoothing = clamp(
            Number(next.cheekSmoothing) || settings.cheekSmoothing || 0.5,
            0,
            0.95
          )
          continue
        }
        if (key === 'faceMeshRender') {
          const allowed = ['none', 'tesselation', 'contours']
          const requested = allowed.includes(next.faceMeshRender)
            ? next.faceMeshRender
            : settings.faceMeshRender
          // Enforce overlay off unless explicitly permitted at engine creation
          settings.faceMeshRender = settings._allowDebugOverlays ? requested : 'none'
          continue
        }
        if (key === 'lidColor') {
          settings.lidColor = typeof next.lidColor === 'string' ? next.lidColor : settings.lidColor
          continue
        }
        if (key === 'irisStyle') {
          // Force vector-only irises; ignore image-based requests
          settings.irisStyle = 'vector'
          continue
        }
        if (key === 'mouthSmoothing') {
          settings.mouthSmoothing = clamp(
            Number(next.mouthSmoothing) || settings.mouthSmoothing || 0.7,
            0,
            0.95
          )
          continue
        }
        if (key === 'mouthBoost') {
          settings.mouthBoost = clamp(Number(next.mouthBoost) || settings.mouthBoost || 1, 0.5, 1.8)
          continue
        }
        if (key === 'gazeDeadzone') {
          settings.gazeDeadzone = clamp(
            Number(next.gazeDeadzone) || settings.gazeDeadzone || 0.04,
            0,
            0.3
          )
          continue
        }
        if (key === 'gazeSmoothing') {
          settings.gazeSmoothing = clamp(
            Number(next.gazeSmoothing) || settings.gazeSmoothing || 0.6,
            0,
            0.98
          )
          continue
        }
        if (
          key === 'gazeOffsetLX' ||
          key === 'gazeOffsetLY' ||
          key === 'gazeOffsetRX' ||
          key === 'gazeOffsetRY'
        ) {
          settings[key] = Number(next[key]) || 0
          continue
        }
        if (
          key === 'gazeGainLX' ||
          key === 'gazeGainLY' ||
          key === 'gazeGainRX' ||
          key === 'gazeGainRY'
        ) {
          const val = Number(next[key])
          settings[key] = Number.isFinite(val) ? clamp(val, 0.4, 2.2) : settings[key]
          continue
        }
        if (key === 'eyeAutoCloseLow') {
          const raw = Number(next.eyeAutoCloseLow)
          const low = Number.isFinite(raw) ? clamp(raw, 0.005, 0.12) : settings.eyeAutoCloseLow
          settings.eyeAutoCloseLow = low
          if ((Number(settings.eyeAutoCloseHigh) || 0) < low + 0.003) {
            settings.eyeAutoCloseHigh = Math.min(0.16, low + 0.003)
          }
          continue
        }
        if (key === 'eyeAutoCloseHigh') {
          const baseLow = Number(settings.eyeAutoCloseLow) || 0.018
          const raw = Number(next.eyeAutoCloseHigh)
          const hi = Number.isFinite(raw) ? clamp(raw, 0.006, 0.16) : settings.eyeAutoCloseHigh
          settings.eyeAutoCloseHigh = Math.max(baseLow + 0.003, hi)
          continue
        }
        if (key === 'lidCloseStart') {
          const v = clamp(Number(next.lidCloseStart) || settings.lidCloseStart || 0.5, 0, 1)
          settings.lidCloseStart = v
          // Ensure full is at least a touch above start
          if ((Number(settings.lidCloseFull) || 0) < v + 0.02) {
            settings.lidCloseFull = Math.min(1, v + 0.02)
          }
          continue
        }
        if (key === 'lidCloseFull') {
          // Respect start; keep a minimal separation
          const start = Number(settings.lidCloseStart) || 0.5
          const raw = Number(next.lidCloseFull)
          const v = Number.isFinite(raw) ? clamp(raw, 0, 1) : settings.lidCloseFull || 0.85
          settings.lidCloseFull = Math.max(start + 0.02, v)
          continue
        }
        if (key === 'irisFreezeStart') {
          const v = clamp(Number(next.irisFreezeStart) || settings.irisFreezeStart || 0.6, 0, 1)
          settings.irisFreezeStart = v
          if ((Number(settings.irisFreezeFull) || 0) < v + 0.02) {
            settings.irisFreezeFull = Math.min(1, v + 0.02)
          }
          continue
        }
        if (key === 'irisFreezeFull') {
          const start = Number(settings.irisFreezeStart) || 0.6
          const raw = Number(next.irisFreezeFull)
          const v = Number.isFinite(raw) ? clamp(raw, 0, 1) : settings.irisFreezeFull || 0.8
          settings.irisFreezeFull = Math.max(start + 0.02, v)
          continue
        }
        settings[key] = next[key]
      }
      if ('adaptiveMinFps' in next || 'adaptiveMaxFps' in next || 'adaptiveTargetFps' in next) {
        const min = clampFps(settings.adaptiveMinFps, 18)
        const max = clampFps(settings.adaptiveMaxFps, 30)
        const lower = Math.min(min, max)
        const upper = Math.max(min, max)
        settings.adaptiveMinFps = lower
        settings.adaptiveMaxFps = upper
        settings.adaptiveTargetFps = clamp(
          clampFps(settings.adaptiveTargetFps, settings.adaptiveTargetFps),
          lower,
          upper
        )
      }
      if (next.layerVisibility && typeof next.layerVisibility === 'object') {
        settings.layerVisibility = {
          ...settings.layerVisibility,
          ...next.layerVisibility
        }
        settings.layerVisibility.background = settings.layerVisibility.background !== false
        settings.layerVisibility.camera = settings.layerVisibility.camera === true
        settings.layerVisibility.avatarFace = settings.layerVisibility.avatarFace !== false
        settings.layerVisibility.avatarHair = settings.layerVisibility.avatarHair !== false
        settings.layerVisibility.avatarEyes = settings.layerVisibility.avatarEyes !== false
        // meshContours removed
        // meshIrises removed
        settings.showCamera = settings.layerVisibility.camera
        settings.avatarHair = settings.layerVisibility.avatarHair
      }
      if (next.eye && typeof next.eye === 'object') {
        settings.eye = { ...settings.eye, ...next.eye }
      }
      if ('onDebug' in next) {
        settings.onDebug = typeof next.onDebug === 'function' ? next.onDebug : null
      }
      if (next.output && typeof next.output === 'object') {
        const prevW = Number(settings.output?.width) || canvas.width
        const prevH = Number(settings.output?.height) || canvas.height
        const prevFps = Number(settings.output?.fps) || DEFAULT_OUTPUT.fps
        const reqW = Number(next.output.width)
        const reqH = Number(next.output.height)
        const reqFps = Number(next.output.fps)

        // Merge settings first
        settings.output = { ...settings.output, ...next.output }

        // Cheap in-place resize when width/height provided
        let targetW = Number.isFinite(reqW) && reqW > 0 ? Math.round(reqW) : null
        let targetH = Number.isFinite(reqH) && reqH > 0 ? Math.round(reqH) : null
        if (targetW !== null || targetH !== null) {
          const srcW = Number(videoEl.videoWidth) || prevW || canvas.width
          const srcH = Number(videoEl.videoHeight) || prevH || canvas.height
          const srcAspect =
            srcW > 0 && srcH > 0 ? srcW / srcH : canvas.width / Math.max(1, canvas.height)
          if (targetW === null && targetH !== null) {
            targetW = Math.max(160, Math.round(targetH * srcAspect))
          }
          if (targetH === null && targetW !== null) {
            targetH = Math.max(120, Math.round(targetW / Math.max(0.1, srcAspect)))
          }
          if (Number.isFinite(targetW) && Number.isFinite(targetH)) {
            const nextW = Math.max(160, targetW)
            const nextH = Math.max(120, targetH)
            if (nextW !== canvas.width || nextH !== canvas.height) {
              let resized = false
              try {
                // This can throw if the canvas was transferred to OffscreenCanvas
                canvas.width = nextW
                canvas.height = nextH
                resized = true
              } catch (e) {
                try {
                  if (settings?.debugLogs) {
                    console.debug(
                      '[AvatarEngine] Skipping canvas resize after Offscreen transfer; using renderer-only resize'
                    )
                  }
                } catch {}
              }
              settings.output.width = nextW
              settings.output.height = nextH
              // Reset segmentation buffers; renderer will rebuild as needed
              segmentedCanvas = null
              segmentedCtx = null
              const gr = geometryManager.getRenderer()
              if (gr) {
                try {
                  gr.setSize(resized ? canvas.width : nextW, resized ? canvas.height : nextH)
                } catch {
                  /* ignore */
                }
              }
            }
          }
        }

        // FPS changes affect throttling in drawFrame; captureStream rate cannot be updated here
        if (Number.isFinite(reqFps) && reqFps > 0 && reqFps !== prevFps) {
          settings.output.fps = reqFps
        }
      }

      const adaptiveBaseTouched =
        'segmentationInterval' in next ||
        'segmentationMaskScale' in next ||
        'meshQuality' in next ||
        'faceUpdateInterval' in next ||
        'adaptiveTargetFps' in next ||
        'adaptiveMinFps' in next ||
        'adaptiveMaxFps' in next ||
        (next.output && 'fps' in next.output)

      if (!settings._adaptiveBase || adaptiveBaseTouched) {
        settings._adaptiveBase = {
          outputFps: Number(settings.output?.fps) || DEFAULT_OUTPUT.fps,
          segInterval: Number(settings.segmentationInterval) || 90,
          maskScale: Number(settings.segmentationMaskScale) || 0.5,
          meshQuality: settings.meshQuality || 'high',
          faceUpdateInterval: Number(settings.faceUpdateInterval) || 40
        }
        settings._adaptiveLevel = 0
      }
      if (settings.adaptiveQuality !== true) {
        settings._adaptiveLevel = 0
      }

      const meshQualityChanged = 'meshQuality' in next && settings.meshQuality !== prevMeshQuality
      const hairEnhanceChanged = 'hairEnhance' in next && settings.hairEnhance !== prevHairEnhance
      const hairEdgeQualityChanged =
        'hairEdgeQuality' in next && settings.hairEdgeQuality !== prevHairEdgeQuality
      const hairMaskSamplingChanged =
        'hairMaskSampling' in next && settings.hairMaskSampling !== prevHairMaskSampling
      const refineHairMaskChanged = 'precomputedAvatarHairMaskSrc' in next
      const mouthRestChanged =
        ('mouthRestClose' in next && (settings.mouthRestClose === true) !== prevMouthRestClose) ||
        ('mouthRestScale' in next &&
          (Number.isFinite(Number(settings.mouthRestScale))
            ? Number(settings.mouthRestScale)
            : null) !== prevMouthRestScale)
      const avatarRebuildRequested =
        forceAvatarRefresh ||
        meshQualityChanged ||
        hairEnhanceChanged ||
        hairEdgeQualityChanged ||
        hairMaskSamplingChanged ||
        refineHairMaskChanged ||
        mouthRestChanged
      const needsAvatarRefresh = 'avatarId' in next || 'avatarSrc' in next || avatarRebuildRequested
      const needsBackgroundRefresh = 'backgroundId' in next || 'backgroundSrc' in next

      if (needsAvatarRefresh || needsBackgroundRefresh) {
        visualUpdatePromise = visualUpdatePromise
          .then(async () => {
            if (needsBackgroundRefresh) await refreshBackgroundAsset({ force: false })
            if (needsAvatarRefresh) await refreshAvatarAssets({ force: avatarRebuildRequested })
          })
          .catch((err) => {
            debug(`Visual update failed: ${err?.message || 'unknown error'}`)
          })
        await visualUpdatePromise
      }

      return { ...settings }
    }

    const blobToDataUrl = (blob) =>
      new Promise((resolve) => {
        try {
          if (!blob || typeof FileReader === 'undefined') {
            resolve(null)
            return
          }
          const reader = new FileReader()
          reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null)
          reader.onerror = () => resolve(null)
          reader.readAsDataURL(blob)
        } catch {
          resolve(null)
        }
      })

    const canvasToDataUrl = async (canvasLike) => {
      if (!canvasLike) return null
      try {
        if (typeof canvasLike.toDataURL === 'function') {
          return canvasLike.toDataURL('image/png')
        }
      } catch {}
      try {
        if (typeof canvasLike.convertToBlob === 'function') {
          const blob = await canvasLike.convertToBlob({ type: 'image/png' })
          return await blobToDataUrl(blob)
        }
      } catch {}
      try {
        if (typeof document !== 'undefined' && typeof createImageBitmap === 'function') {
          const w = Math.max(1, Number(canvasLike.width) || 1)
          const h = Math.max(1, Number(canvasLike.height) || 1)
          const domCanvas = document.createElement('canvas')
          domCanvas.width = w
          domCanvas.height = h
          const domCtx = domCanvas.getContext('2d')
          if (!domCtx) return null
          const bmp = await createImageBitmap(canvasLike)
          domCtx.drawImage(bmp, 0, 0, w, h)
          try {
            if (typeof bmp.close === 'function') bmp.close()
          } catch {}
          return domCanvas.toDataURL('image/png')
        }
      } catch {}
      return null
    }

    const getAvatarLayerPreview = async () => {
      if (!avatarLayers) return null
      // Always recompute to reflect current cutout and settings.
      // Cache caused mismatches with inspector; disable for accuracy here.
      // For previews we want face WITHOUT eyes; prefer faceNoIrisCanvas
      const faceCanvasCandidate = avatarLayers?.faceNoIrisCanvas || avatarLayers?.faceCanvas || null

      const buildFilledFace = () => {
        const base = faceCanvasCandidate
        const sclera = avatarLayers?.scleraCanvas || null
        const iris = avatarLayers?.irisCanvas || null
        const w =
          Number(avatarRig?.imageWidth) ||
          Number(base?.width) ||
          Number(avatarLayers?.hairCanvas?.width) ||
          0
        const h =
          Number(avatarRig?.imageHeight) ||
          Number(base?.height) ||
          Number(avatarLayers?.hairCanvas?.height) ||
          0
        if (!(w > 0 && h > 0) || !base) return base
        // If there is no sclera/iris layer, return base as-is
        if (!sclera && !iris) return base
        try {
          const c = document.createElement('canvas')
          c.width = w
          c.height = h
          const cx = c.getContext('2d')
          if (!cx) return base
          if (sclera) cx.drawImage(sclera, 0, 0, w, h)
          if (iris) cx.drawImage(iris, 0, 0, w, h)
          cx.drawImage(base, 0, 0, w, h)
          return c
        } catch {
          return base
        }
      }
      const faceFilledCanvas = buildFilledFace()

      const buildHeadComposite = () => {
        const face = faceFilledCanvas || faceCanvasCandidate
        const hair = avatarLayers?.hairCanvas || null
        const w = Number(avatarRig?.imageWidth) || Number(face?.width) || Number(hair?.width) || 0
        const h =
          Number(avatarRig?.imageHeight) || Number(face?.height) || Number(hair?.height) || 0
        if (!(w > 0 && h > 0) || !face) return face
        try {
          const c = document.createElement('canvas')
          c.width = w
          c.height = h
          const cx = c.getContext('2d')
          if (!cx) return face
          cx.drawImage(face, 0, 0, w, h)
          if (hair) cx.drawImage(hair, 0, 0, w, h)
          return c
        } catch {
          return face
        }
      }
      const headComposite = buildHeadComposite()
      const preview = {
        baseSrc: currentAvatarSrc || null,
        source: avatarLayers?.source || '',
        width:
          Number(avatarRig?.imageWidth) ||
          Number(faceCanvasCandidate?.width) ||
          Number(avatarLayers?.hairCanvas?.width) ||
          0,
        height:
          Number(avatarRig?.imageHeight) ||
          Number(faceCanvasCandidate?.height) ||
          Number(avatarLayers?.hairCanvas?.height) ||
          0,
        stats: {
          hairOpaque: Number(avatarLayers?.hairOpaque || 0),
          faceOpaque: Number(avatarLayers?.faceOpaque || 0)
        },
        layers: {
          // Only the layers we actually use at runtime composition time
          hair:
            Number(avatarLayers?.hairOpaque || 0) > 16 && avatarLayers?.hairCanvas
              ? await canvasToDataUrl(avatarLayers?.hairCanvas)
              : null,
          face: await canvasToDataUrl(faceCanvasCandidate),
          // Add selected cutout - ALWAYS mirror the cutout used by renderer
          selected: await (async () => {
            try {
              // 1) Use the same cutout path that the live renderer uses
              const selectedCutout = buildAvatarCutoutByMode()
              if (selectedCutout) return await canvasToDataUrl(selectedCutout)

              // 2) Fallbacks for resilience (should rarely trigger)
              if (!avatarRig || avatarLayers?._fallback === true) {
                const borderCutout = buildBorderAvatarCutout()
                if (borderCutout) return await canvasToDataUrl(borderCutout)
              }
              if (headComposite) return await canvasToDataUrl(headComposite)
              if (faceFilledCanvas) return await canvasToDataUrl(faceFilledCanvas)

              if (avatarImage?.img) {
                const fallbackCanvas = document.createElement('canvas')
                fallbackCanvas.width = avatarImage.img.naturalWidth || avatarImage.img.width
                fallbackCanvas.height = avatarImage.img.naturalHeight || avatarImage.img.height
                const fallbackCtx = fallbackCanvas.getContext('2d')
                if (fallbackCtx) {
                  fallbackCtx.drawImage(avatarImage.img, 0, 0)
                  return await canvasToDataUrl(fallbackCanvas)
                }
              }
              return null
            } catch (err) {
              console.debug && console.debug('[getAvatarLayerPreview] selected cutout error', err)
              return null
            }
          })()
        }
      }
      layerPreviewCacheVersion = geometryAssetsVersion
      layerPreviewCache = preview
      return preview
    }

    const stop = () => {
      try {
        if (renderLoop) renderLoop.stop()
      } catch {}
      try {
        setRenderBudgetActive('avatar', false)
      } catch {}
      try {
        videoEl.pause()
      } catch {
        /* ignore */
      }
      try {
        if (audioContext && audioContext.state !== 'closed') audioContext.close()
      } catch {
        /* ignore */
      }
      try {
        const gr = geometryManager.getRenderer()
        if (gr) {
          gr.dispose()
        }
      } catch {
        /* ignore */
      }
      try {
        if (resourceManager && typeof resourceManager.dispose === 'function')
          resourceManager.dispose()
      } catch {}
      // clear large buffers and runtime-produced mesh outputs to help GC
      try {
        geometryCanonicalBuffer = null
        geometryCanonicalSmoothed = null
        warpCanonicalBuffer = null
        warpDepthBuffer = null
        warpDstPoints = null
        eyeWarpDstPoints = null
        eyeSocketDstPoints = null
        trackedLiveCanonical = null
        trackedFacialSignals = null
        fittedCanonical = null
        layerPreviewCache = null
        layerPreviewCacheVersion = -1
      } catch (e) {}
      if (inputStream && typeof inputStream.getTracks === 'function') {
        inputStream.getTracks().forEach((track) => track.stop())
      }
    }

    return { stream: outputStream, stop, updateSettings, getAvatarLayerPreview }
  }
}

// Re-exports for testing and external access (keeps internals reachable without importing subpaths)
export {
  DEFAULT_OUTPUT,
  LANDMARKS,
  FACE_MESH_POINT_COUNT,
  FACE_GEOMETRY_ANCHORS,
  NOSE_FEATURE_INDICES,
  CHEEK_FEATURE_INDICES,
  JAW_EAR_FEATURE_INDICES,
  clamp,
  lerp,
  clampByte,
  meshQualityToStride
}

export {
  createPointArray,
  computeFaceMetrics,
  toCanonical,
  canonicalToCanvas,
  applyRegionScale,
  applyRegionScaleAsymY,
  pickCanonicalPoint,
  getEyeCenter,
  avgY,
  translateIndicesY,
  translateIndicesWeighted,
  selectNearestIndices,
  readIrisCenter,
  computeEyeGaze,
  boostWeightsNear,
  buildExpressionWeights,
  buildFeatureWrapProfile,
  applyFeatureAwareWrap,
  decimateTriangles,
  computeFacialSignals
}

export { buildAvatarRig }

export {
  buildFaceMeshAlpha,
  buildPolygonAlphaMask,
  dilateMask,
  mergeMasksMax,
  buildEyeFallbackAlpha,
  createLayerFromAlpha,
  createScleraLayerFromAlpha,
  sampleIrisPalette,
  countOpaque,
  filterTrianglesByAlpha,
  buildBorderBackgroundAlpha
}

export {
  resolveAvatarSrc,
  resolveBackgroundSrc,
  loadSafeImage,
  resolveOutputDimensions,
  waitForVideoMetadata
}

export {
  eyesClipToEyeSockets as clipToEyeSockets,
  eyesFillEyeSockets as fillEyeSockets,
  eyesDrawPupilCircles as drawPupilCircles,
  eyesComputeIrisStats as computeIrisStats,
  eyesDrawEyelidOccluder as drawEyelidOccluder
}

// Export derived feature index sets defined earlier in this module
export { LIP_FEATURE_INDICES, EYE_FEATURE_INDICES, BROW_FEATURE_INDICES }

// Geometry and mesh helpers
export { default as createGeometryManager } from './avatarEngine/geometry'
export { getFaceMeshTriangles, warpTriangles }
export { FACEMESH_TRIANGULATION }
// Mesh warper / renderer utilities (re-export existing implementations)
export { createMeshWarper } from './avatarEngine/meshWarper'
export { createMeshRenderer } from './avatarEngine/meshRenderer'

export default new AvatarEngine()

// Re-export vision utilities/stage for external usage
// Export a lazy wrapper for initVision to preserve public API while keeping it lazy-loaded
export async function initVision(...args) {
  const mod = await import('./avatarEngine/vision')
  if (typeof mod.initVision === 'function') return mod.initVision(...args)
  if (mod.default && typeof mod.default.initVision === 'function')
    return mod.default.initVision(...args)
  throw new Error('initVision not found in ./avatarEngine/vision')
}
export { createVisionStage }
export { createTracker }

